import { createContext, ReactNode, useCallback, useContext, useState } from "react";

import { ToastInterface } from "@prequel/shared-components";

type ToastContextType = {
  toasts: ToastInterface[];
  addToast: (toast: Omit<ToastInterface, "id">) => void;
  dismissToast: (toastId: string) => void;
};

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastInterface[]>([]);

  const dismissToast = useCallback((toastId: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== toastId));
  }, []);

  const addToast = useCallback(
    (toast: Omit<ToastInterface, "id">) => {
      const newToast: ToastInterface = {
        ...toast,
        id: new Date().getTime().toString(),
      };
      setToasts((prev) => [...prev, newToast]);

      // Auto-dismiss after 5 seconds unless disabled
      if (!toast.autoDismissDisabled) {
        setTimeout(() => {
          dismissToast(newToast.id);
        }, 5000);
      }
    },
    [dismissToast],
  );

  return <ToastContext.Provider value={{ toasts, addToast, dismissToast }}>{children}</ToastContext.Provider>;
}

export function useToast() {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}
